import { Color } from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextStyle from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';
import ImageResize from 'tiptap-extension-resize-image';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit, { StarterKitOptions } from '@tiptap/starter-kit';
import React, { memo, useEffect, useMemo } from 'react';

import {
	defaultBlockquoteOptions,
	defaultBulletListOptions,
	defaultHeadingOptions,
	defaultOrderedListOptions,
} from '@/ui/Editor/defaulOptions';
import { FontSize, HeadingOptions, Headings, Image } from '@/ui/Editor/extensions';
import { Tag } from '@/ui/Editor/extensions/Tag';
import { TagList } from '@/ui/Editor/extensions/Tag/TagList';
import { TagOptions } from '@/ui/Editor/extensions/Tag/types';
import { useTagExtension } from '@/ui/Editor/extensions/Tag/useTagExtension';

import { MenuBar } from './components/MenuBar';
import { cn } from '@/utils';

export interface EditorOptions {
	blockquote?: StarterKitOptions['blockquote'];
	bold?: StarterKitOptions['bold'];
	bulletList?: StarterKitOptions['bulletList'];
	history?: StarterKitOptions['history'];
	horizontalRule?: StarterKitOptions['horizontalRule'];
	italic?: StarterKitOptions['italic'];
	orderedList?: StarterKitOptions['orderedList'];
	strike?: StarterKitOptions['strike'];
	headings?: Partial<HeadingOptions> | false;
	fontSize?: false;
	link?: false;
	tag?: TagOptions | false;
}
export interface EditorProps {
	value: string;
	disabled?: boolean;
	onChange?: (value: string) => void;
	placeholder?: string;
	options?: EditorOptions;
	textAlign?: boolean;
	editable?: boolean;
}

const Editor = memo(({ value, onChange, disabled, placeholder, textAlign, editable = true, options = {} }: EditorProps) => {
	const normalizeHtml = React.useCallback((html?: string | null) => {
		const normalized = (html || '').trim();
		return normalized === '<p></p>' ? '' : normalized;
	}, []);


	const extensions = useMemo(() => {
		const {
			blockquote = defaultBlockquoteOptions,
			bold,
			bulletList = defaultBulletListOptions,
			orderedList = defaultOrderedListOptions,
			history,
			horizontalRule,
			headings = defaultHeadingOptions,
			italic,
			strike,
			fontSize,
			link,
			tag = false,
		} = options;
		const headingsEnabled = headings !== false;
		const fontSizeEnabled = fontSize !== false;
		const tagEnabled = tag !== false;

		return [
			StarterKit.configure({
				blockquote,
				bold,
				bulletList,
				orderedList,
				hardBreak: false,
				history,
				horizontalRule,
				italic,
				strike,
				code: false,
				codeBlock: false,
				heading: false,
			}),
			...(link !== false
				? [
						Link.configure({
							openOnClick: false,
							validate: (href) => /^https?:\/\//.test(href),
							HTMLAttributes: {
								class: 'underline text-blue-600 hover:text-blue-800 visited:text-purple-600 hover:cursor-pointer',
							},
						}),
				  ]
				: []),
			...(headingsEnabled ? [Headings.configure(headings)] : []),
			...(fontSizeEnabled ? [FontSize] : []),
			...(tagEnabled ? [Tag.configure(tag)] : []),
			TextStyle,
			FontFamily,
			Color,
			Image,
			Placeholder.configure({
				placeholder,
			}),
			TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
			ImageResize
		];
	}, [placeholder, options]);

	const editor = useEditor({
		extensions,
		content: value,
		editable: editable,
		onUpdate() {
			if (editor) {
				const currentValue = editor.isEmpty ? '' : editor.getHTML();
				if (onChange) {
					onChange(currentValue);
				}
			}
		},
	});

	useEffect(() => {
		if (!editor) {
			return;
		}

		const nextValue = normalizeHtml(value);
		const currentValue = normalizeHtml(editor.isEmpty ? '' : editor.getHTML());

		if (currentValue === nextValue) {
			return;
		}

		if (!nextValue) {
			editor.commands.clearContent(false);
			return;
		}

		editor.commands.setContent(nextValue, false);
	}, [editor, normalizeHtml, value]);

	/**
	 * @desc Used to save initial editor state on render to then compare it against new state on save to handle images upload & deletion to server
	 */
	useEffect(() => {
		// TODO: ilia orlov - use useSessionStore npm lib instead of vanilla to have SSR, serialization, and sync between browser tabs
		sessionStorage.setItem('editor-initial-content', value);
		return () => {
			sessionStorage.removeItem('editor-initial-content');
		};
	}, []);

	const { handleDrop, tagEnabled, tagList } = useTagExtension({ editor, tag: options.tag });
	const classBorder = editable ? 'rounded-b border-b border-l border-r border-gray-300' : '';

	return (
		<div>
			{editor && editable && <MenuBar editor={editor} textAlign={textAlign} />}
			<div className={cn('p-2.5  min-h-60', classBorder)}>
				<EditorContent
					className="min-h-60"
					editor={editor}
					placeholder={placeholder}
					disabled={disabled}
					onDrop={handleDrop}
				/>
			</div>
			{tagEnabled && editable && <TagList editor={editor} tags={tagList} />}
		</div>
	);
});

export { Editor };
